import React from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Col, Row } from "antd";
import { IDeepDiveModalConfig } from "@/ts/common-types";
import AgGridLoaderComponent from "../loaders/AgGridLoaderComponent";
import WidgetCard from "./WidgetCard";

interface IChartProps {
    title?: string;
    widgetData: IDeepDiveModalConfig["spendRoasData"];
}

const ProductRoasSpendWidget: React.FC<IChartProps> = ({
    title,
    widgetData,
}) => {
    const chartRefs = React.useRef([]);

    const getChartOptions = (
        title: string,
        categories: Array<string>,
        series: Array<{
            name?: string;
            data?: number[];
            color?: string;
        }>,
        borderRight: boolean = false
    ) => ({
        chart: {
            type: "bar"
        },
        title: {
            text: title,
            align: "center",
            style: {
                fontSize: "15px",
                fontWeight: "normal"
            },
        },
        xAxis: {
            categories,
            title: {
                text: "",
            },
            lineWidth: 1,
            lineColor: "#535353",
        },
        yAxis: [
            {
                title: {
                    text: "Spend",
                    enabled: false,
                },
                labels: {
                    overflow: "justify",
                },
                gridLineWidth: 1,
                visible: true,
            },
            {
                title: {
                    text: "ROAS",
                    align: "high",
                },
                labels: {
                    overflow: "justify",
                },
                opposite: true,
                gridLineWidth: 0,
                visible: false,
            },
        ],
        plotOptions: {
            bar: {
                borderRadius: "0%",
                dataLabels: {
                    enabled: true,
                },
                groupPadding: 0.1,
            },
        },
        legend: {
            layout: "horizontal",
            align: "center",
            verticalAlign: "top",
            backgroundColor:
                Highcharts.defaultOptions.legend.backgroundColor || "#FFFFFF",
            enabled: false
        },
        credits: {
            enabled: false,
        },
        series,
    });

    return (
        <WidgetCard className="mt-4">
            <Row
                className="fs-5 fw-bold mb-3"
                justify="space-between"
                align="middle"
            >
                <Col className="d-flex align-items-center justify-content-between w-100">
                    <div><h5>{title || "ROAS vs. Spends by Ad Type"}</h5></div>

                    <div className="d-flex align-items-center justify-content-around">
                        <div className="chart-legend d-flex align-items-center ms-5 fw-normal">
                            <div className="chart-legend-dot chart-legend-dot-color-by-spend"></div>
                            By Spend
                        </div>
                        <div className="chart-legend d-flex align-items-center ms-5 fw-normal">
                            <div className="chart-legend-dot chart-legend-dot-color-by-roas"></div>
                            By ROAS
                        </div>
                    </div>


                </Col>
            </Row>
            {
            widgetData ? (
                <Row gutter={16}>
                    <Col span={8}>
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={getChartOptions(
                                "By Ad Type",
                                widgetData?.chartDataSeries?.byAdType
                                    ?.categories || [],
                                widgetData?.chartDataSeries?.byAdType
                                    ?.series || [{}],
                                true
                            )}
                            ref={(el) => (chartRefs.current[0] = el)}
                        />
                    </Col>
                    <Col span={8}>
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={getChartOptions(
                                "By Target Type",
                                widgetData?.chartDataSeries?.byTargetType
                                    ?.categories || [],
                                widgetData?.chartDataSeries?.byTargetType
                                    ?.series || [{}],
                                true
                            )}
                            ref={(el) => (chartRefs.current[1] = el)}
                        />
                    </Col>
                    <Col span={8}>
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={getChartOptions(
                                "By Match Type",
                                widgetData?.chartDataSeries?.byMatchType
                                    ?.categories || [],
                                widgetData?.chartDataSeries?.byMatchType
                                    ?.series || [{}]
                            )}
                            ref={(el) => (chartRefs.current[2] = el)}
                        />
                    </Col>
                </Row>
            ) : (
                <div
                    className="d-flex align-items-center justify-content-center"
                    style={{ minHeight: "300px" }}
                >
                    <span
                        style={{
                            transform: "translateX(-50%)",
                        }}
                    >
                        <AgGridLoaderComponent />
                    </span>
                </div>
            )
        }
        </WidgetCard >
    );
};

export default ProductRoasSpendWidget;